//= require n2words/dist/n2words.js
import { Controller } from '@hotwired/stimulus';

export default class SubscriptionConfirmationController extends Controller {
  static targets = [
    'form',
    'amountInput',
    'amountInWordsInput',
    'inProgressIcon',
    'cancelIcon',
    'checkIcon',
    'confirmButton',
    'warningModal',
    'confirmationModal'
  ];

  static values = {amountInWords: String}

  connect() {
    if (this.hasAmountInWordsInputTarget && this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
    }
  }

  limitInputLength(event) {
    const AMOUNT_LENGTH_LIMIT = 9;
    if (event.target.value.length > AMOUNT_LENGTH_LIMIT) {
      event.target.value = event.target.value.slice(0, AMOUNT_LENGTH_LIMIT);
    }
  }

  onAmountKeyup() {
    this.syncAmountInWords();
    this.validateAmountInWords();
  }

  syncAmountInWords() {
    const amount = parseInt(this.amountInputTarget.value, 10) || 0;

    if (amount === 0) {
      this.amountInWordsValue = '';
      return;
    }

    this.amountInWordsInputTarget.removeAttribute('disabled');
    this.amountInWordsValue = n2words(amount, { lang: getCurrentLocale() });
  }

  validateAmountInWords() {
    if (!this.hasAmountInWordsInputTarget) return;

    const amountTyped = this.amountInWordsInputTarget.value;

    if (amountTyped.length <= 2) {
      [this.inProgressIconTarget, this.cancelIconTarget, this.checkIconTarget].forEach(el => el.classList.add('d-none'));
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
      return;
    }

    const formatted = this.formatAmountText(this.amountInWordsValue);
    const typed = this.formatAmountText(amountTyped);

    if (formatted === typed) {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.remove('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false;
    } else if (formatted.substring(0, typed.length) === typed) {
      this.inProgressIconTarget.classList.remove('d-none');
      this.cancelIconTarget.classList.add('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    } else {
      this.inProgressIconTarget.classList.add('d-none');
      this.cancelIconTarget.classList.remove('d-none');
      this.checkIconTarget.classList.add('d-none');
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
    }
  }

  checkAndConfirm(event) {
    if (!this.hasAmountInputTarget) return;
    if (!this.hasFormTarget) return;

    if (!this.formTarget.reportValidity()) return;

    this.pendingForm = this.formTarget;

    const lossesCapacity = this.amountInputTarget.dataset.losses_capacity;
    const amount = parseInt(this.amountInputTarget.value, 10);

    if (isNaN(amount)) return;

    if (lossesCapacity && amount > parseInt(lossesCapacity, 10)) {
      new bootstrap.Modal(this.warningModalTarget).show();
    } else {
      this.showConfirmationModal();
    }
  }

  handleWarningAccepted() {
    bootstrap.Modal.getInstance(this.warningModalTarget).hide();
    this.showConfirmationModal();
  }

  handleConfirmSubmit() {
    bootstrap.Modal.getInstance(this.confirmationModalTarget).hide();
    if (this.pendingForm) {
      this.pendingForm.submit();
    }
  }

  showConfirmationModal() {
    const currentLocale = getCurrentLocale();
    const amountEl = this.confirmationModalTarget.querySelector('#amount');
    const projectEl = this.confirmationModalTarget.querySelector('#project');
    const subscriptionAmount = parseInt(this.amountInputTarget.value, 10);
    const selectedCurrency = this.confirmationModalTarget.dataset.projectCurrency;
    const formatter = new Intl.NumberFormat(currentLocale, { style: 'currency', currency: selectedCurrency });
    amountEl.innerText = formatter.format(subscriptionAmount);
    if (projectEl) {
      projectEl.innerText = this.confirmationModalTarget.dataset.projectName;
    }
    new bootstrap.Modal(this.confirmationModalTarget).show();
  }

  formatAmountText(text) {
    return text.normalize('NFD').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

}
